import {
    ResponsiveModal,
    ResponsiveModalBody,
    ResponsiveModalContent,
    ResponsiveModalFooter,
    ResponsiveModalHeader,
    ResponsiveModalTitle,
} from '@/components/ui/responsive-modal';
import { Button } from '@/components/ui/shadcn/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/shadcn/card';
import { Input } from '@/components/ui/shadcn/input';
import { Label } from '@/components/ui/shadcn/label';
import { useTranslations } from '@/hooks/useTranslations';
import AppLayout from '@/layouts/app-layout';
import SettingsLayout from '@/layouts/settings/layout';
import axios from 'axios';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

interface Category {
    id: string;
    name: string;
    sort_order: number;
}

export default function PurchaseOrderSettings() {
    const { t } = useTranslations();
    const [categories, setCategories] = useState<Category[]>([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState<Category | null>(null);
    const [nameInput, setNameInput] = useState('');
    const [saving, setSaving] = useState(false);

    const load = () => {
        setLoading(true);
        axios
            .get('/api/purchase-order-item-categories')
            .then((res) => setCategories(res.data))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        load();
    }, []);

    const openCreate = () => {
        setEditing(null);
        setNameInput('');
        setDialogOpen(true);
    };

    const openEdit = (cat: Category) => {
        setEditing(cat);
        setNameInput(cat.name);
        setDialogOpen(true);
    };

    const handleSave = () => {
        if (!nameInput.trim()) return;
        setSaving(true);

        const req = editing
            ? axios.patch(`/api/purchase-order-item-categories/${editing.id}`, { name: nameInput.trim() })
            : axios.post('/api/purchase-order-item-categories', { name: nameInput.trim() });

        req.then(() => {
            toast.success(
                editing
                    ? t('toasts.settings_purchaseordersettings.category_updated', 'Category updated.')
                    : t('toasts.settings_purchaseordersettings.category_created', 'Category created.'),
            );
            setDialogOpen(false);
            load();
        })
            .catch((err) => {
                const msg =
                    err.response?.data?.errors?.name?.[0] ?? t('toasts.settings_purchaseordersettings.save_failed', 'Failed to save category.');
                toast.error(msg);
            })
            .finally(() => setSaving(false));
    };

    const handleDelete = (cat: Category) => {
        if (!confirm(`Delete category "${cat.name}"? Items using this category will lose the assignment.`)) return;
        axios
            .delete(`/api/purchase-order-item-categories/${cat.id}`)
            .then(() => {
                toast.success(t('toasts.settings_purchaseordersettings.category_deleted', 'Category deleted.'));
                load();
            })
            .catch(() => toast.error(t('toasts.settings_purchaseordersettings.delete_failed', 'Failed to delete category.')));
    };

    return (
        <AppLayout>
            <SettingsLayout>
                <div className="space-y-6">
                    <Card>
                        <CardHeader>
                            <div className="flex items-center justify-between">
                                <div>
                                    <CardTitle>Line Item Categories</CardTitle>
                                    <CardDescription className="mt-1">
                                        Categories help organise purchase order line items. Assigned per line when creating or editing a PO.
                                    </CardDescription>
                                </div>
                                <Button size="sm" onClick={openCreate}>
                                    <Plus className="mr-1 h-4 w-4" />
                                    Add Category
                                </Button>
                            </div>
                        </CardHeader>
                        <CardContent>
                            {loading ? (
                                <p className="text-muted-foreground text-sm">Loading...</p>
                            ) : categories.length === 0 ? (
                                <p className="text-muted-foreground py-6 text-center text-sm">No categories yet. Add one to get started.</p>
                            ) : (
                                <ul className="divide-y">
                                    {categories.map((cat) => (
                                        <li key={cat.id} className="flex items-center justify-between py-2.5">
                                            <span className="text-sm font-medium">{cat.name}</span>
                                            <div className="flex gap-1">
                                                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(cat)}>
                                                    <Pencil className="h-3.5 w-3.5" />
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="text-destructive hover:text-destructive h-7 w-7"
                                                    onClick={() => handleDelete(cat)}
                                                >
                                                    <Trash2 className="h-3.5 w-3.5" />
                                                </Button>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </CardContent>
                    </Card>
                </div>

                <ResponsiveModal open={dialogOpen} onOpenChange={setDialogOpen} variant="sheet">
                    <ResponsiveModalContent size="md">
                        <ResponsiveModalHeader>
                            <ResponsiveModalTitle>{editing ? 'Edit Category' : 'New Category'}</ResponsiveModalTitle>
                        </ResponsiveModalHeader>
                        <ResponsiveModalBody className="space-y-2">
                            <Label htmlFor="cat-name">Name</Label>
                            <Input
                                id="cat-name"
                                value={nameInput}
                                onChange={(e) => setNameInput(e.target.value)}
                                placeholder="e.g. Electrical, Mechanical, Raw Materials..."
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleSave();
                                }}
                            />
                        </ResponsiveModalBody>
                        <ResponsiveModalFooter>
                            <Button variant="outline" onClick={() => setDialogOpen(false)}>
                                Cancel
                            </Button>
                            <Button onClick={handleSave} disabled={saving || !nameInput.trim()}>
                                {saving ? 'Saving...' : 'Save'}
                            </Button>
                        </ResponsiveModalFooter>
                    </ResponsiveModalContent>
                </ResponsiveModal>
            </SettingsLayout>
        </AppLayout>
    );
}
